import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { canManageGames } from '../../domain/games/policy';
import { GameList } from '../../features/games/GameList';
import { GameTagFilter } from '../../features/games/GameTagFilter';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectAllGames, selectFavoriteGameIds } from '../../store/selectors/gamesSelectors';
import { fetchFavoriteGames, fetchGames, fetchGameTags } from '../../store/thunks/gamesThunks';

import './GamesPage.css';

export function GamesPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const games = useAppSelector(selectAllGames);
  const favoriteIds = useAppSelector(selectFavoriteGameIds);
  const role = useAppSelector((s) => s.auth.user?.role);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [showFavorites, setShowFavorites] = useState(false);

  useEffect(() => {
    dispatch(fetchGames());
    dispatch(fetchGameTags());
    dispatch(fetchFavoriteGames());
  }, [dispatch]);

  const filteredGames = useMemo(() => {
    let result = games;

    if (showFavorites) {
      result = result.filter((g) => favoriteIds.includes(g.id));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((g) => g.name.toLowerCase().includes(q));
    }

    if (activeTags.length > 0) {
      result = result.filter((g) =>
        activeTags.some((tag) => g.tags.some((t) => t.name === tag)),
      );
    }

    return result;
  }, [games, searchQuery, activeTags, showFavorites, favoriteIds]);

  const handleCardTagClick = useCallback(
    (tag: string) => {
      setActiveTags((prev) =>
        prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
      );
    },
    [],
  );

  const canManage = canManageGames(role);

  return (
    <div className="games-page">
      <div className="games-page__header">
        <h1 className="games-page__title">Каталог игр</h1>
        {canManage && (
          <button
            className="tavern-btn-secondary games-page__add-btn"
            onClick={() => navigate('/games/new')}
          >
            + Добавить
          </button>
        )}
      </div>

      <div className="games-page__filters">
        <div className="games-page__search">
          <input
            className="games-page__search-input"
            type="text"
            placeholder="Поиск по названию..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="games-page__search-clear"
              onClick={() => setSearchQuery('')}
            >
              ×
            </button>
          )}
        </div>

        <div className="games-page__filter-row">
          <button
            className={`game-tag game-tag--filter${showFavorites ? ' game-tag--active' : ''}`}
            onClick={() => setShowFavorites(!showFavorites)}
          >
            ❤️ Избранные
          </button>
        </div>

        <GameTagFilter selectedTags={activeTags} onTagsChange={setActiveTags} />
      </div>

      <div className="games-page__content">
        {filteredGames.length === 0 ? (
          <div className="tavern-empty-state">
            <div className="tavern-empty-state-icon">🎲</div>
            <div className="tavern-empty-state-title">Игры не найдены</div>
            <div className="tavern-empty-state-description">
              {searchQuery || activeTags.length > 0 || showFavorites
                ? 'Попробуйте изменить параметры поиска'
                : 'Каталог пока пуст'}
            </div>
          </div>
        ) : (
          <GameList
            games={filteredGames}
            activeTags={activeTags}
            onTagClick={handleCardTagClick}
          />
        )}
      </div>
    </div>
  );
}
